import React from 'react';
import { observer } from 'mobx-react-lite';

import { InlineTimeSelector } from './InlineTimeSelector';
import { minutesFormatter } from '../../../lib/time-formatters';
import { Step } from '../../../app/entities/step';
import { Stripboard } from '../../../app/entities/stripboard';
import { formatDate } from '../../../lib/utils/DateHelper';
import { dateShort } from '../../../assets/contants/dates-fns';
import { BaseRow, Cell, BaseBlock, RowMirror } from './styled-strips';

import type { DayBreakStrip } from '../../../app/entities/strip';
import { DayStartStripCore } from './day-start-strip';
import { useCurrentStep } from '../../../hooks/use-current-step';

import styles from './day-break-strip.module.scss';
export type DayBreakStripProps = {
  stripboard: Stripboard;
  isShotList?: boolean;
  strip: DayBreakStrip;
};

export const DayBreakStripComponent = observer(
  React.forwardRef<HTMLTableRowElement, DayBreakStripProps>(
    ({ isShotList, stripboard, strip, ...draggableProps }, ref) => {
      const { dod, stepId, endTime, totalEstimatedTime, totalShotsDuration } = strip.data || {};
      const step = useCurrentStep();

      const shootingDay = Step.getOne(stepId)!;
      const totalShootingDays = stripboard.totalShootingDays;

      if (!shootingDay) return null;

      const formattedDate = shootingDay?.dueDate ? formatDate(shootingDay.dueDate, dateShort) : '';

      const nextShootingDayStrip =
        totalShootingDays > 0 &&
        dod !== undefined &&
        dod < totalShootingDays &&
        strip.position !== undefined
          ? stripboard.getNextShootingDay(strip._id)
          : undefined;

      const nextShootingDay =
        step?.type !== 'shootingDay' && nextShootingDayStrip?.data
          ? Step.getOne(nextShootingDayStrip.data.stepId)
          : undefined;

      return (
        <BaseRow
          className={styles['row']}
          ref={ref}
          data-has-start={!!nextShootingDayStrip}
          {...draggableProps}
        >
          <Cell
            colSpan={100}
            style={{
              padding: 0,
            }}
          >
            <RowMirror data-shotlist={isShotList}>
              <BaseBlock className={styles['end-of-day']}>
                End of day{' '}
                {totalShootingDays && (
                  <>
                    {dod} of {totalShootingDays.toString()}
                  </>
                )}
                <span className={styles['date-wrapper']}>{formattedDate}</span>
              </BaseBlock>

              <div className={styles['cell-mirror']}>
                <div className={styles['block-wrapper']}>{!isShotList && <span>Total</span>}</div>
              </div>

              {isShotList && (
                <>
                  <div className={styles['cell-mirror']}>Total</div>

                  <div className={styles['cell-mirror']}>
                    {minutesFormatter(totalShotsDuration)}
                  </div>

                  <div className={styles['cell-mirror']} />
                </>
              )}

              {!isShotList && (
                <>
                  <div className={styles['cell-mirror']}>
                    {minutesFormatter(totalEstimatedTime)}
                  </div>

                  <div className={styles['cell-mirror']}>
                    <InlineTimeSelector time={endTime || { hour: 7, minute: 0 }} disabled />
                  </div>
                </>
              )}
            </RowMirror>

            {nextShootingDayStrip && nextShootingDay && (
              <DayStartStripCore
                stripboard={stripboard}
                strip={nextShootingDayStrip}
                startDate={nextShootingDay.dueDate}
                isShotList={isShotList}
                shootingDay={nextShootingDay}
              />
            )}
          </Cell>
        </BaseRow>
      );
    },
  ),
);
